import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ApplicationLayout from 'v3/layouts/Application';
import Button from 'v3/components/Button';
import Text from 'v3/components/Text';
import useModal from 'hooks/useModal';
import api, { cooperplaceApi } from 'services/api';
import { usePermission } from 'hooks';
import Section from '../shared/Section';
import Data from '../shared/Data/index';
import Tracking from '../shared/Tracking/index';
import Settings from '../shared/Settings/index';
import Automations from '../shared/Automations/index';
import Addresses from '../shared/Addresses/index';
import PriorityFleet from '../shared/PriorityFleet/index';
import Checklist from '../Details/Checklist';
import Activities from '../shared/Activities/index';
import OccurrenceTypes from '../Details/OccurrenceTypes';
import CreatedClientModal from './Modal/index';
import { validate } from '../shared/validator';
import { useClient } from '../shared/context';

function normalizeIe(rawIe) {
  if (typeof rawIe !== 'string') return rawIe || null;

  const normalizedIe = rawIe.trim().toUpperCase();
  return normalizedIe || null;
}

function buildStateRegistration(rawIe) {
  const normalizedIe = normalizeIe(rawIe);

  if (!normalizedIe) return null;
  if (normalizedIe === 'ISENTO') return normalizedIe;

  return normalizedIe;
}

export default function ClientRegister() {
  usePermission('CADASTRAR_CLIENTES', { redirect: true });
  const navigate = useNavigate();
  const client = useClient();
  const createdClientModal = useModal();

  const [isLoading, setLoading] = useState(false);

  const buildOccurrenceTypesPayload = (occurrenceClientTypes = []) => (
    occurrenceClientTypes.map((type) => ({
      id: type.id,
      name: type.name,
      slug: type.slug,
      app_visible: type.app_visible,
      color: type.color,
      order: type.order,
      occurrenceFieldsConfigs: (type.occurrenceFieldsConfigs || []).map((field) => ({
        id: field.id,
        name: field.name,
        format: field.format ?? null,
        type: field.type,
        status: {
          id: field?.status?.id ?? field?.statusId ?? 0,
          name: field?.status?.name ?? 'Opcional',
          slug: field?.status?.slug ?? 'OPTIONAL',
        },
      })),
    }))
  );

  async function handleCreation(setData, setErrors) {
    setLoading(true);
    client.setShowRequiredErrors(true);
    const isValid = await validate(
      'allSchema',
      {
        data: client.data,
        settings: client.data.settings,
        addresses: client.data.addresses,
        tracking: {
          sending_tracking: client?.data?.client?.sending_tracking,
          total_send_email: client?.data?.client?.total_send_email?.id,
          emails_tracking: client?.data?.client?.emails_tracking,
          cost_centers: client?.data?.client?.cost_centers?.map(item => item?.id) || [],
          time_interval: client?.data?.client?.time_interval || [],
          enable_logic_panel: client?.data?.client?.enable_logic_panel
        },
      },
      client.setErrors,
    );
    if (!isValid) {
      client.showSnackbar('Verifique os campos obrigatórios', {
        type: 'error',
      });
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const normalizedIe = normalizeIe(client.data.ie);
      const stateRegistration = buildStateRegistration(client.data.ie);

      const payload = {
        orderRequired: !!client.data?.orderRequired,
        data: {
          type_id: client.data?.personType?.name === 'Física' ? 2 : 3,
          is_international: client.data.is_international,
          is_rewards_member: client.data.is_rewards_member,
          social_name: client.data.social_name,
          cgccpf: client.data.cgccpf,
          phone: client.data.phone,
          ie: normalizedIe,
          state_registration: stateRegistration,
          fantasy_name: client.data.fantasy_name,
          is_client: true,
          is_taker: true,
          email: client.data.email,
          taker_role_id: client.data?.takerRole?.code,
          client_type_id: client.data?.clientType?.id,
          credit_limit: client.data?.credit_limit || 1000000,
        },
        tags: client.data.clientTags,
        settings: client.data.settings,
        automations: client.data.clientAutomations,
        addresses: client.data?.addresses,
        priorityFleetVehicles: client.data?.priorityFleetVehicles,
        checklists: client.data?.companyPerson?.checklists,
        occurrenceTypes: buildOccurrenceTypesPayload(client.data?.occurrenceClientTypes || []),
        cfops: client.data.cfops,
      };

      const response = await api.post('v3/client', payload);

      try {
        await cooperplaceApi.put(
          `clients/tracking/${response.data.id}/emails`,
          {
            sending_tracking: client.data.client.sending_tracking,
            emails_tracking: client.data.client.emails_tracking,
            cost_centers: client.data.client.cost_centers?.map(item => item?.id) || [],
            total_send_email: client.data.client.total_send_email?.id || null,
            time_interval: client.data.client.time_interval || [],
            enable_logic_panel: client.data.client.enable_logic_panel,
          }
        );
      } catch (trackingError) {
        client.showSnackbar(
          trackingError?.response?.data?.message || 'Cliente cadastrado, mas houve falha ao salvar o tracking',
          { type: 'warning' },
        );
      }

      client.showSnackbar('Cliente cadastrado', { type: 'success' });
      setLoading(false);
      client.setData({ id: response.data.id });
      createdClientModal.open();
    } catch (err) {
      let message = err?.response?.data?.message;
      setLoading(false);
      if (err.response) {
        const errors = err?.response?.data;
        if (Array.isArray(errors)) {
          setErrors((old) => ({
            ...old,
            ...Object.fromEntries(
              errors?.map((error) => {
                if (
                  error?.field === 'data.ie' &&
                  error?.validation === 'requiredWhenNot'
                ) {
                  return [error.field, 'Inscrição estadual obrigatória'];
                }

                return [error.field, error.message];
              }),
            ),
          }));
          message =
            errors[0]?.field === 'data.ie' && errors[0]?.validation === 'requiredWhenNot'
              ? 'Inscrição estadual obrigatória'
              : errors[0]?.message;
        } else {
          message = errors.error;
          setErrors(errors.error);
        }
      }

      client.showSnackbar(message || 'Erro ao cadastrar cliente!', {
        type: 'error',
      });
    }
  }

  return (
    <ApplicationLayout
      title="Cliente"
      RightComponent={
        <>
          <Button variant="secondary" onClick={() => navigate('/clientes')}>
            <Text weight={500} color="black" type="regular">
              Voltar
            </Text>
          </Button>

          <Button
            variant="success"
            onClick={() => {
              handleCreation(client.setData, client.setErrors);
            }}
            loading={isLoading}
          >
            <Text weight={500} color="white" type="regular">
              Salvar
            </Text>
          </Button>

        </>
      }
    >
      <CreatedClientModal isOpen={createdClientModal.isOpen} onClose={createdClientModal.close} />
      <Row className="row-eq-height">
        <Col xs={12} md={6}>
          <Section Title="Dados gerais">
            <Data />
          </Section>
        </Col>
        <Col xs={12} md={6}>
          <OccurrenceTypes />
          <Section Title="Configuração">
            <Settings />
          </Section>
          <Section Title="Automações">
            <Automations />
          </Section>
          <Section Title="Envio de Tracking">
            <Tracking />
          </Section>
        </Col>
        <Col md={12}>
          <Addresses />
          <PriorityFleet />
        </Col>
        <Col xs={12} md={6}>
          <Checklist />
        </Col>
        <Col xs={12} md={6}>
          <Section Title="Tipo da atividade" className="full-height-card">
            <Activities />
          </Section>
        </Col>
      </Row>
    </ApplicationLayout>
  );
}
